import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import blogService from '../../services/blogService';
import Icon from '../../components/AppIcon';
import ArticleCard from './components/ArticleCard';
import TopicFilter from './components/TopicFilter';

const PublicBlog = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadPublishedArticles();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [articles, selectedTopic, searchQuery, sortBy]);

  const loadPublishedArticles = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getPublishedArticles();
      setArticles(data);
    } catch (err) {
      setError(err?.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...articles];

    // Topic filter
    if (selectedTopic !== 'all') {
      filtered = filtered?.filter(article => article?.topic === selectedTopic);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        article =>
          article?.title?.toLowerCase()?.includes(query) ||
          article?.excerpt?.toLowerCase()?.includes(query)
      );
    }

    // Sort
    if (sortBy === 'newest') {
      filtered?.sort((a, b) => new Date(b?.publishedAt || b?.createdAt) - new Date(a?.publishedAt || a?.createdAt));
    } else if (sortBy === 'oldest') {
      filtered?.sort((a, b) => new Date(a?.publishedAt || a?.createdAt) - new Date(b?.publishedAt || b?.createdAt));
    } else if (sortBy === 'popular') {
      filtered?.sort((a, b) => (b?.viewCount || 0) - (a?.viewCount || 0));
    }

    setFilteredArticles(filtered);
  };

  const handleArticleClick = (slug) => {
    navigate(`/blog-article-view/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading articles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-12" />
          <p className="text-destructive mb-16">{error}</p>
          <button
            onClick={loadPublishedArticles}
            className="px-16 py-8 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-16">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-12">
              <button
                onClick={() => navigate('/landing-page')}
                className="flex items-center gap-8 hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center justify-center w-10 h-10 bg-primary/20 rounded-lg">
                  <Icon name="DollarSign" size={24} color="var(--color-primary)" strokeWidth={2.5} />
                </div>
                <span className="text-lg font-heading font-semibold">Rovyn Budget</span>
              </button>
            </div>
            <button
              onClick={() => navigate('/landing-page')}
              className="flex items-center gap-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 py-20 md:py-24">
        <div className="mx-auto px-4 md:px-6 lg:px-24 max-w-7xl text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-12">
            Financial Insights & Expertise
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-12">
            Explore our collection of articles on personal finance, investment strategies, budgeting tips, and market analysis.
          </p>
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 max-w-3xl mx-auto">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Educational Disclaimer:</strong> This content is for educational purposes only and does not constitute financial advice. Always consider your personal situation or consult a qualified professional.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-20 md:py-24">
        {/* Topic Filter */}
        <TopicFilter selectedTopic={selectedTopic} onTopicChange={setSelectedTopic} />

        {/* Search & Sort Bar */}
        <div className="flex flex-col md:flex-row gap-12 mb-20">
          <div className="flex-1 relative">
            <Icon
              name="Search"
              size={20}
              className="absolute left-12 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-40 pr-12 py-10 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-12 py-10 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>

        {/* Articles Grid */}
        {filteredArticles?.length === 0 ? (
          <div className="text-center py-32">
            <Icon name="FileText" size={64} className="text-muted-foreground mx-auto mb-16" />
            <h3 className="text-xl font-semibold mb-8">No articles found</h3>
            <p className="text-muted-foreground">
              {searchQuery || selectedTopic !== 'all' ?'Try adjusting your filters or search query' :'Check back soon for new content'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20">
            {filteredArticles?.map((article) => (
              <ArticleCard
                key={article?.id}
                article={article}
                onClick={() => handleArticleClick(article?.slug)}
              />
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-primary to-secondary py-20 md:py-24">
        <div className="mx-auto px-4 md:px-6 lg:px-24 max-w-4xl text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-12">
            Ready to Take Control of Your Finances?
          </h2>
          <p className="text-base md:text-lg text-white/90 mb-20">
            Join thousands of users who trust Rovyn Budget to manage their money with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-12 justify-center mb-16">
            <button
              onClick={() => navigate('/register')}
              className="px-24 py-12 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-colors shadow-lg"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/landing-page')}
              className="px-24 py-12 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20"
            >
              Learn More
            </button>
          </div>
          <p className="text-sm text-white/80 italic">
            Rovyn Budget does not provide individualized financial advice.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PublicBlog;